<template>
  <div class="w-full space-y-4 pb-6 sm:pb-8">
    <!-- Stepper Navigation Header Cards -->
    <div class="grid gap-2" :style="{ gridTemplateColumns: `repeat(${activeSteps.length}, minmax(0, 1fr))` }">
      <div v-for="(st, idx) in activeSteps" :key="st.key" @click="canJumpToStep(idx) && (currentStep = idx)" :class="[
        idx === currentStep
          ? 'bg-indigo-50 border-indigo-200 ring-2 ring-indigo-500/20 shadow-xs'
          : idx < currentStep
            ? 'bg-emerald-50/70 border-emerald-200 cursor-pointer'
            : 'bg-white border-gray-100 opacity-60',
        'p-2.5 sm:p-3 rounded-xl border transition-all text-center flex flex-col items-center justify-center'
      ]">
        <div class="flex items-center justify-center mb-1">
          <v-icon v-if="idx < currentStep" x-small color="emerald">mdi-check-circle</v-icon>
          <v-icon v-else x-small :color="idx === currentStep ? 'indigo' : 'grey'">{{ st.icon }}</v-icon>
        </div>
        <span class="text-[11px] sm:text-xs font-bold truncate max-w-full"
          :class="idx === currentStep ? 'text-indigo-900' : idx < currentStep ? 'text-emerald-900' : 'text-gray-500'">
          {{ st.title }}
        </span>
      </div>
    </div>

    <!-- Form Body Card -->
    <div class="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
      <!-- STEP 1: Case & Subcase Selection -->
      <div v-if="currentStepKey === 'case'" class="space-y-4">
        <div class="border-b border-gray-100 pb-3 flex items-center justify-between">
          <div>
            <h3 class="text-xs sm:text-sm font-bold text-gray-900 mb-0.5">Select Subcase & Case</h3>
            <p class="text-[11px] text-gray-500 mb-0">Search existing case or enter a new case number.</p>
          </div>
        </div>

        <!-- Subcase Selector -->
        <div v-if="subCaseOptions && subCaseOptions.length > 0">
          <label class="block text-xs font-bold text-gray-700 mb-1">
            Sub-Case / Category <span class="text-rose-500">*</span>
          </label>
          <v-select v-model="form.subCaseId" :items="subCaseOptions" item-text="label" item-value="value" dense outlined
            placeholder="Select Subcase" class="rounded-xl text-xs font-medium" hide-details="auto"
            @change="handleSubCaseChange"></v-select>
        </div>

        <!-- Case Number Search / Add UI with Vuetify 2 -->
        <div v-if="form.subCaseId" class="space-y-2">
          <label class="block text-xs font-bold text-gray-700">
            {{ fieldMappings.caseNo.label }} <span v-if="fieldMappings.caseNo.required" class="text-rose-500">*</span>
          </label>

          <!-- Selected Case Indicator Card -->
          <div v-if="isExistingCaseSelected && selectedExistingCase"
            class="bg-indigo-50/70 border border-indigo-200 rounded-2xl p-3.5 flex items-center justify-between gap-3 shadow-xs">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-xs shrink-0">
                <v-icon small color="white">mdi-file-document-check-outline</v-icon>
              </div>
              <div class="space-y-0.5">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-black text-indigo-950 font-mono">{{ selectedExistingCase.caseNo }}</span>
                  <span
                    class="text-[10px] font-bold px-1.5 py-0.2 bg-emerald-100 text-emerald-800 rounded">Active</span>
                </div>
                <p class="text-[11px] text-gray-600 font-medium mb-0">
                  {{ form.customerFullName || 'No customer name' }} • {{ form.customerMobile || 'No phone' }}
                </p>
                <p v-if="form.pos || form.emiAmount" class="text-[11px] text-indigo-700 font-bold mb-0">
                  POS: ₹{{ formatAmount(form.pos) }} • EMI: ₹{{ formatAmount(form.emiAmount) }}
                </p>
              </div>
            </div>
            <button type="button"
              class="px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-50 transition-all cursor-pointer shrink-0"
              @click="clearCaseSelection">
              Change
            </button>
          </div>

          <!-- Search Input Box with Realtime Results Popover -->
          <div v-else class="relative">
            <v-text-field v-model="caseSearchQuery"
              :placeholder="`Type to search ${fieldMappings.caseNo.label}, customer name, or phone...`"
              prepend-inner-icon="mdi-magnify" dense outlined hide-details="auto" class="rounded-xl text-xs font-medium"
              @focus="isSearchDropdownOpen = true" @input="handleCaseSearchInput">
              <!-- Quick Add Action Button in Vuetify 2 append slot -->
              <template v-if="caseSearchQuery && caseSearchQuery.trim().length > 0" #append>
                <v-btn depressed x-small :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text font-bold rounded-lg px-2.5 -mr-1"
                  height="28" @click.stop="onAddNewCaseNo(caseSearchQuery)">
                  <v-icon left x-small>mdi-plus</v-icon>
                  Add New
                </v-btn>
              </template>
            </v-text-field>

            <!-- Search Results Dropdown Card -->
            <div
              v-if="isSearchDropdownOpen && (loadingCases || existingCaseOptions.length > 0 || (caseSearchQuery && caseSearchQuery.trim()))"
              class="absolute left-0 right-0 top-full mt-1.5 bg-white border border-gray-200 rounded-2xl shadow-xl z-30 max-h-60 overflow-y-auto divide-y divide-gray-100">
              <!-- Loading State -->
              <div v-if="loadingCases"
                class="p-3 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                <v-progress-circular indeterminate size="16" width="2" color="indigo"></v-progress-circular>
                Searching active cases...
              </div>

              <!-- Case Search Results List -->
              <template v-else-if="existingCaseOptions.length > 0">
                <div v-for="c in existingCaseOptions" :key="c._id"
                  class="p-2.5 hover:bg-indigo-50/60 transition-all cursor-pointer flex items-center justify-between"
                  @click="onSelectExistingCase(c)">
                  <div class="space-y-0.5">
                    <div class="flex items-center gap-2">
                      <span class="text-xs font-bold text-indigo-900 font-mono">{{ c.caseNo }}</span>
                      <span v-if="c.typeOfLoan"
                        class="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.2 rounded font-medium">{{ c.typeOfLoan
                        }}</span>
                    </div>
                    <p class="text-[11px] text-gray-600 mb-0 font-medium">
                      {{ c.customer?.customerFullName || c.customerFullName || 'No name' }} • {{
                        c.customer?.customerMobile?.[0] || c.mobile || 'No phone' }}
                    </p>
                  </div>
                  <div class="text-right">
                    <span v-if="c.pos" class="text-xs font-bold text-emerald-700 block">₹{{ formatAmount(c.pos)
                      }}</span>
                    <span class="text-[10px] text-indigo-600 font-bold">Select</span>
                  </div>
                </div>
              </template>

              <!-- Option to Create Brand New Case Number -->
              <div v-if="caseSearchQuery && caseSearchQuery.trim()"
                class="p-3 bg-gray-50 hover:bg-indigo-50 transition-all cursor-pointer flex items-center justify-between text-indigo-700 font-bold text-xs"
                @click="onAddNewCaseNo(caseSearchQuery)">
                <div class="flex items-center gap-2">
                  <v-icon small color="indigo">mdi-plus-circle</v-icon>
                  <span>Create new case with number: "<strong>{{ caseSearchQuery }}</strong>"</span>
                </div>
                <span class="text-[11px] bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-md font-semibold">New
                  Case</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Extra Case Fields (Rendered ONLY when creating a brand NEW case number) -->
        <div v-if="form.subCaseId && caseFieldsVisible && hasAnyVisibleCaseField"
          class="space-y-3 pt-2 border-t border-dashed border-gray-200">
          <div class="flex items-center justify-between">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500">New Case Financial Details</span>
          </div>

          <!-- Entity / Bank -->
          <div v-if="fieldMappings.entity.label && fieldMappings.entity.visible">
            <label class="block text-xs font-bold text-gray-700 mb-1">
              {{ fieldMappings.entity.label }}
              <span v-if="fieldMappings.entity.required" class="text-rose-500">*</span>
            </label>
            <v-select v-model="form.entity" :items="entityOptions" item-text="label" item-value="value" dense outlined
              :placeholder="`Select ${fieldMappings.entity.label}`" class="rounded-xl text-xs font-medium"
              hide-details="auto"></v-select>
          </div>

          <!-- Loan Type & Bucket -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div v-if="fieldMappings.typeOfLoan.label && fieldMappings.typeOfLoan.visible">
              <label class="block text-xs font-bold text-gray-700 mb-1">
                {{ fieldMappings.typeOfLoan.label }}
                <span v-if="fieldMappings.typeOfLoan.required" class="text-rose-500">*</span>
              </label>
              <v-select v-model="form.typeOfLoan" :items="loanTypeOptions" dense outlined
                :placeholder="`Select ${fieldMappings.typeOfLoan.label}`" class="rounded-xl text-xs font-medium"
                hide-details="auto"></v-select>
            </div>
            <div v-if="fieldMappings.bkt.label && fieldMappings.bkt.visible">
              <label class="block text-xs font-bold text-gray-700 mb-1">
                {{ fieldMappings.bkt.label }}
                <span v-if="fieldMappings.bkt.required" class="text-rose-500">*</span>
              </label>
              <v-select v-model="form.bkt" :items="bucketOptions" dense outlined
                :placeholder="`Select ${fieldMappings.bkt.label}`" class="rounded-xl text-xs font-medium"
                hide-details="auto"></v-select>
            </div>
          </div>

          <!-- POS & EMI Amount -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div v-if="fieldMappings.pos.label && fieldMappings.pos.visible">
              <label class="block text-xs font-bold text-gray-700 mb-1">
                {{ fieldMappings.pos.label }} (₹)
                <span v-if="fieldMappings.pos.required" class="text-rose-500">*</span>
              </label>
              <v-text-field v-model="form.pos" dense outlined type="number" placeholder="e.g. 25000"
                class="rounded-xl text-xs font-medium" hide-details="auto"></v-text-field>
            </div>
            <div v-if="fieldMappings.emiAmount.label && fieldMappings.emiAmount.visible">
              <label class="block text-xs font-bold text-gray-700 mb-1">
                {{ fieldMappings.emiAmount.label }} (₹)
                <span v-if="fieldMappings.emiAmount.required" class="text-rose-500">*</span>
              </label>
              <v-text-field v-model="form.emiAmount" dense outlined type="number" placeholder="e.g. 3500"
                class="rounded-xl text-xs font-medium" hide-details="auto"></v-text-field>
            </div>
          </div>

          <!-- Product Info & Tenure -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div v-if="fieldMappings.productInfo.label && fieldMappings.productInfo.visible">
              <label class="block text-xs font-bold text-gray-700 mb-1">
                {{ fieldMappings.productInfo.label }}
                <span v-if="fieldMappings.productInfo.required" class="text-rose-500">*</span>
              </label>
              <v-text-field v-model="form.productInfo" dense outlined
                :placeholder="`Enter ${fieldMappings.productInfo.label}`" class="rounded-xl text-xs font-medium"
                hide-details="auto"></v-text-field>
            </div>
            <div v-if="fieldMappings.tenure.label && fieldMappings.tenure.visible">
              <label class="block text-xs font-bold text-gray-700 mb-1">
                {{ fieldMappings.tenure.label }}
                <span v-if="fieldMappings.tenure.required" class="text-rose-500">*</span>
              </label>
              <v-text-field v-model="form.tenure" dense outlined type="number"
                :placeholder="`Enter ${fieldMappings.tenure.label}`" class="rounded-xl text-xs font-medium"
                hide-details="auto"></v-text-field>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 2: Customer Details -->
      <div v-else-if="currentStepKey === 'customer'" class="space-y-4">
        <div class="border-b border-gray-100 pb-3 flex items-center justify-between">
          <div>
            <h3 class="text-xs sm:text-sm font-bold text-gray-900 mb-0.5">{{ customerStepTitle }}</h3>
            <p class="text-[11px] text-gray-500 mb-0">Search existing customer or enter new customer details.</p>
          </div>
        </div>

        <!-- Customer Search / Select Box -->
        <div class="space-y-2">
          <label class="block text-xs font-bold text-gray-700">
            Search / Add Customer <span v-if="fieldMappings.customerFullName.required" class="text-rose-500">*</span>
          </label>

          <!-- Selected Customer Indicator Card -->
          <div v-if="isExistingCustomerSelected && selectedCustomer"
            class="bg-indigo-50/70 border border-indigo-200 rounded-2xl p-3.5 flex items-center justify-between gap-3 shadow-xs">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-xs shrink-0">
                <v-icon small color="white">mdi-account-check-outline</v-icon>
              </div>
              <div class="space-y-0.5">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-black text-indigo-950">{{ form.customerFullName || 'Selected Customer'
                    }}</span>
                  <span
                    class="text-[10px] font-bold px-1.5 py-0.2 bg-emerald-100 text-emerald-800 rounded">Existing</span>
                </div>
                <p class="text-[11px] text-gray-600 font-medium mb-0">
                  Phone: {{ form.customerMobile || 'No phone' }} <span v-if="form.crn">• CRN: {{ form.crn }}</span>
                </p>
                <p v-if="form.addressLine1 || form.city"
                  class="text-[11px] text-indigo-700 font-medium mb-0 truncate max-w-xs">
                  {{ form.addressLine1 }} {{ form.city }} {{ form.pincode }}
                </p>
              </div>
            </div>
            <button type="button"
              class="px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-50 transition-all cursor-pointer shrink-0"
              @click="clearCustomerSelection">
              Change
            </button>
          </div>

          <!-- Customer Search Input Box with Realtime Results Popover -->
          <div v-else class="relative">
            <v-text-field v-model="customerSearchQuery" placeholder="Type to search customer by name, phone, or CRN..."
              prepend-inner-icon="mdi-magnify" dense outlined hide-details="auto" class="rounded-xl text-xs font-medium"
              @focus="isCustomerDropdownOpen = true" @input="handleCustomerSearchInput">
              <template v-if="customerSearchQuery && customerSearchQuery.trim().length > 0" #append>
                <v-btn depressed x-small :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text font-bold rounded-lg px-2.5 -mr-1"
                  height="28" @click.stop="onAddNewCustomer(customerSearchQuery)">
                  <v-icon left x-small>mdi-plus</v-icon>
                  Add New
                </v-btn>
              </template>
            </v-text-field>

            <!-- Customer Search Results Dropdown -->
            <div
              v-if="isCustomerDropdownOpen && (loadingCustomers || customerList.length > 0 || (customerSearchQuery && customerSearchQuery.trim()))"
              class="absolute left-0 right-0 top-full mt-1.5 bg-white border border-gray-200 rounded-2xl shadow-xl z-30 max-h-60 overflow-y-auto divide-y divide-gray-100">
              <!-- Loading State -->
              <div v-if="loadingCustomers"
                class="p-3 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                <v-progress-circular indeterminate size="16" width="2" color="indigo"></v-progress-circular>
                Searching customers...
              </div>

              <!-- Customer Results List -->
              <template v-else-if="customerList.length > 0">
                <div v-for="c in customerList" :key="c._id"
                  class="p-2.5 hover:bg-indigo-50/60 transition-all cursor-pointer flex items-center justify-between"
                  @click="onSelectCustomer(c)">
                  <div class="space-y-0.5">
                    <div class="flex items-center gap-2">
                      <span class="text-xs font-bold text-indigo-900">{{ c.customerFullName || `${c.firstName || ''}
                        ${c.lastName || ''}`.trim() || 'Unnamed' }}</span>
                      <span v-if="c.crn"
                        class="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.2 rounded font-medium">{{ c.crn
                        }}</span>
                    </div>
                    <p class="text-[11px] text-gray-600 mb-0 font-medium">
                      {{ c.customerMobile?.[0] || c.mobile || 'No phone' }}
                      <span v-if="c.customerAddress?.[0]?.city"> • {{ c.customerAddress[0].city }}</span>
                    </p>
                  </div>
                  <div class="text-right">
                    <span class="text-[10px] text-indigo-600 font-bold">Select</span>
                  </div>
                </div>
              </template>

              <!-- Option to Create Brand New Customer -->
              <div v-if="customerSearchQuery && customerSearchQuery.trim()"
                class="p-3 bg-gray-50 hover:bg-indigo-50 transition-all cursor-pointer flex items-center justify-between text-indigo-700 font-bold text-xs"
                @click="onAddNewCustomer(customerSearchQuery)">
                <div class="flex items-center gap-2">
                  <v-icon small color="indigo">mdi-plus-circle</v-icon>
                  <span>Add new customer: "<strong>{{ customerSearchQuery }}</strong>"</span>
                </div>
                <span class="text-[11px] bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-md font-semibold">+ New
                  Customer</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Divider with OR in center -->
        <div v-if="!isExistingCustomerSelected || customerFieldsVisible" class="relative my-4">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-200"></div>
          </div>
          <div class="relative flex justify-center text-xs">
            <span class="bg-white px-3 font-bold uppercase tracking-wider text-gray-400">OR</span>
          </div>
        </div>

        <!-- Manual Customer Input Fields (Shown when adding new or when not selecting existing) -->
        <div v-if="!isExistingCustomerSelected || customerFieldsVisible" class="space-y-3">
          <!-- Customer Full Name -->
          <div v-if="fieldMappings.customerFullName">
            <label class="block text-xs font-bold text-gray-700 mb-1">
              {{ fieldMappings.customerFullName.label }} <span v-if="fieldMappings.customerFullName.required"
                class="text-rose-500">*</span>
            </label>
            <v-text-field v-model="form.customerFullName" dense outlined
              :placeholder="`Enter ${fieldMappings.customerFullName.label}`" class="rounded-xl text-xs font-medium"
              hide-details="auto"></v-text-field>
          </div>

          <!-- CRN / Customer ID with Auto Generate and Refresh button -->
          <div v-if="fieldMappings.crn">
            <div class="flex items-center justify-between mb-1">
              <label class="block text-xs font-bold text-gray-700 mb-0">
                {{ fieldMappings.crn.label }} <span v-if="fieldMappings.crn.required" class="text-rose-500">*</span>
              </label>
              <span class="text-[10px] text-gray-500 font-medium">Auto-generated</span>
            </div>
            <v-text-field v-model="form.crn" dense outlined
              :placeholder="`Enter or generate ${fieldMappings.crn.label}`" class="rounded-xl text-xs font-medium"
              hide-details="auto">
              <template #append>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" class="-mr-1" @click="form.crn = generateCRN()">
                      <v-icon small color="indigo">mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>Regenerate CRN</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </div>

          <!-- Customer Mobile & Alternate Mobile -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div v-if="fieldMappings.customerMobile">
              <label class="block text-xs font-bold text-gray-700 mb-1">
                {{ fieldMappings.customerMobile.label }} <span v-if="fieldMappings.customerMobile.required"
                  class="text-rose-500">*</span>
              </label>
              <v-text-field v-model="form.customerMobile" dense outlined placeholder="e.g. 9876543210" type="tel"
                maxlength="10" class="rounded-xl text-xs font-medium" hide-details="auto"
                :rules="mobileRules" @input="onMobileInput('customerMobile', $event)"></v-text-field>
            </div>
            <div v-if="fieldMappings.customerAlternateMobile">
              <label class="block text-xs font-bold text-gray-700 mb-1">
                {{ fieldMappings.customerAlternateMobile.label }} <span
                  v-if="fieldMappings.customerAlternateMobile.required" class="text-rose-500">*</span>
              </label>
              <v-text-field v-model="form.customerAlternateMobile" dense outlined placeholder="e.g. 9123456780"
                type="tel" maxlength="10" class="rounded-xl text-xs font-medium" hide-details="auto"
                :rules="altMobileRules" @input="onMobileInput('customerAlternateMobile', $event)"></v-text-field>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 3: Address & Location -->
      <div v-else-if="currentStepKey === 'address'" class="space-y-4">
        <div class="border-b border-gray-100 pb-3">
          <h3 class="text-xs sm:text-sm font-bold text-gray-900 mb-0.5">{{ addressStepTitle }}</h3>
          <p class="text-[11px] text-gray-500 mb-0">Provide complete field visit address for map navigation.</p>
        </div>

        <!-- Street Address / Address Line 1 -->
        <div v-if="fieldMappings.address.visible">
          <label class="block text-xs font-bold text-gray-700 mb-1">
            {{ fieldMappings.address.label }} <span v-if="fieldMappings.address.required" class="text-rose-500">*</span>
          </label>
          <v-textarea v-model="form.addressLine1" rows="2" dense outlined
            placeholder="Flat / House No, Street name, Landmark" class="rounded-xl text-xs font-medium"
            hide-details="auto"></v-textarea>
        </div>

        <!-- City, State, Pincode -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div v-if="fieldMappings.city.visible">
            <label class="block text-xs font-bold text-gray-700 mb-1">
              {{ fieldMappings.city.label }} <span v-if="fieldMappings.city.required" class="text-rose-500">*</span>
            </label>
            <v-text-field v-model="form.city" dense outlined placeholder="e.g. Mumbai"
              class="rounded-xl text-xs font-medium" hide-details="auto"></v-text-field>
          </div>
          <div v-if="fieldMappings.state.visible">
            <label class="block text-xs font-bold text-gray-700 mb-1">
              {{ fieldMappings.state.label }} <span v-if="fieldMappings.state.required" class="text-rose-500">*</span>
            </label>
            <v-text-field v-model="form.state" dense outlined placeholder="e.g. Maharashtra"
              class="rounded-xl text-xs font-medium" hide-details="auto"></v-text-field>
          </div>
          <div v-if="fieldMappings.pincode.visible">
            <label class="block text-xs font-bold text-gray-700 mb-1">
              {{ fieldMappings.pincode.label }} <span v-if="fieldMappings.pincode.required"
                class="text-rose-500">*</span>
            </label>
            <v-text-field v-model="form.pincode" dense outlined maxlength="6" placeholder="e.g. 400001"
              class="rounded-xl text-xs font-medium" hide-details="auto"></v-text-field>
          </div>
        </div>

        <div class="bg-indigo-50/50 rounded-xl p-3 border border-indigo-100 flex items-start gap-2.5">
          <v-icon small color="indigo" class="mt-0.5">mdi-map-marker-radius</v-icon>
          <div>
            <span class="text-xs font-bold text-indigo-900 block">GPS Coordinates for Navigation</span>
            <span class="text-[11px] text-gray-600">The mobile app will automatically resolve GPS routes to this
              address.</span>
          </div>
        </div>
      </div>

      <!-- STEP 4: Task Schedule & Review -->
      <div v-else-if="currentStepKey === 'schedule'" class="space-y-4">
        <div class="border-b border-gray-100 pb-3">
          <h3 class="text-xs sm:text-sm font-bold text-gray-900 mb-0.5">Task Title & Appointment Timings</h3>
          <p class="text-[11px] text-gray-500 mb-0">Set task schedule and review details before creating.</p>
        </div>

        <!-- Task Title -->
        <div v-if="stepperStepsConfig.taskTitle?.visibility !== false">
          <label class="block text-xs font-bold text-gray-700 mb-1">Task Title <span
              v-if="stepperStepsConfig.taskTitle?.required !== false" class="text-rose-500">*</span></label>
          <v-text-field v-model="form.taskTitle" dense outlined placeholder="e.g. Field Visit / Document Pickup"
            class="rounded-xl text-xs font-medium" hide-details="auto"></v-text-field>
        </div>

        <!-- Description / Special Notes -->
        <div>
          <label class="block text-xs font-bold text-gray-700 mb-1">Visit Notes / Description</label>
          <v-textarea v-model="form.taskDescription" rows="2" dense outlined
            placeholder="Special instructions for the visit..." class="rounded-xl text-xs font-medium"
            hide-details="auto"></v-textarea>
        </div>

        <!-- Schedule Start & End Datetime -->
        <div v-if="stepperStepsConfig.caseStartAndEndTime?.visibility !== false" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Start Date & Time <span
                v-if="stepperStepsConfig.caseStartAndEndTime?.required !== false" class="text-rose-500">*</span></label>
            <v-text-field v-model="form.taskStartDateTime" type="datetime-local" dense outlined
              prepend-inner-icon="mdi-calendar-clock" class="rounded-xl text-xs font-medium"
              hide-details="auto"></v-text-field>
          </div>
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">End Date & Time <span
                v-if="stepperStepsConfig.caseStartAndEndTime?.required !== false" class="text-rose-500">*</span></label>
            <v-text-field v-model="form.taskEndDateTime" type="datetime-local" dense outlined
              prepend-inner-icon="mdi-calendar-clock" class="rounded-xl text-xs font-medium"
              hide-details="auto"></v-text-field>
          </div>
        </div>

        <!-- Review Summary Box -->
        <div class="bg-gray-50 rounded-xl p-3.5 border border-gray-200 text-xs space-y-1.5">
          <span class="font-bold text-gray-800 block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Quick
            Summary</span>
          <div class="flex justify-between">
            <span class="text-gray-500">Sub-Case:</span>
            <span class="font-bold text-indigo-900">{{ selectedSubCaseLabel }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">Customer:</span>
            <span class="font-bold text-gray-900">{{ form.customerFullName || '-' }} ({{ form.customerMobile || '-'
              }})</span>
          </div>
          <div v-if="form.caseNo" class="flex justify-between">
            <span class="text-gray-500">{{ fieldMappings.caseNo.label }}:</span>
            <span class="font-bold text-indigo-700 font-mono">{{ form.caseNo }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">Address:</span>
            <span class="font-semibold text-gray-800 text-right truncate max-w-[220px]">{{ form.addressLine1 || '-' }},
              {{
                form.city || '-' }}</span>
          </div>
          <div v-if="form.pos" class="flex justify-between">
            <span class="text-gray-500">{{ fieldMappings.pos.label }}:</span>
            <span class="font-bold text-emerald-700">₹ {{ formatAmount(form.pos) }}</span>
          </div>
        </div>
      </div>

      <!-- Bottom Step Navigation Buttons -->
      <div class="flex items-center justify-between pt-5 mt-4 border-t border-gray-100">
        <v-btn v-if="currentStep > 0" outlined small color="grey darken-2" class="rounded-xl h-9 text-xs font-bold px-4"
          @click="prevStep">
          <v-icon left small>mdi-chevron-left</v-icon>
          Previous
        </v-btn>
        <div v-else></div>

        <v-btn v-if="currentStep < activeSteps.length - 1" depressed small :color="$vuetify.theme.currentTheme.employeePrimary"
          class="white--text rounded-xl h-9 text-xs font-bold px-5 shadow-xs" @click="nextStep">
          Next
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>

        <v-btn v-else depressed small :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-xl h-9 text-xs font-bold px-6 shadow-xs"
          :loading="submitting" @click="submitTask">
          <v-icon left small>mdi-check-circle-outline</v-icon>
          Create
        </v-btn>
      </div>
    </div>

    <!-- Success Creation Dialog (Matching Mobile App Flow) -->
    <v-dialog v-model="successModalOpen" persistent max-width="420">
      <v-card class="rounded-2xl p-6 text-center shadow-xl border border-gray-100">
        <div
          class="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4 border border-emerald-100 shadow-xs">
          <v-icon size="36" color="emerald darken-1">mdi-check-circle</v-icon>
        </div>

        <h3 class="text-base sm:text-lg font-black text-gray-900 mb-1">Task Created Successfully!</h3>
        <p class="text-xs text-gray-500 mb-6">
          Task for <strong class="text-gray-800">{{ createdTaskInfo?.title || form.taskTitle || form.customerFullName
            }}</strong> has been scheduled. Would you like to proceed with the task now?
        </p>

        <div class="flex flex-col gap-2.5">
          <v-btn depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text font-bold text-xs h-10 rounded-xl shadow-xs"
            @click="proceedWithTask">
            <v-icon left small>mdi-play-circle-outline</v-icon>
            Proceed with Task
          </v-btn>

          <v-btn outlined color="grey darken-2" class="font-bold text-xs h-10 rounded-xl border-gray-200"
            @click="goToDashboard">
            <v-icon left small>mdi-home-outline</v-icon>
            Go to Dashboard
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import {
  getCustomers,
  createCustomer,
  createCollectionCase,
  createCollectionCaseTask,
  updateCaseStatus,
  createNewTask,
  getEntities,
  getAllUseCases,
  getExtendUseCaseById,
  getCollectionCases,
  getEmpAppSettings,
  getTaskSettings,
  getOrgConfig
} from '@/app/services/dashboard'
import { convertNumberToAmount } from '@/app/utils/helpers'
import { computeTaskSteps } from '@/app/utils/taskFlow'

export default {
  name: 'AppTaskCreate',
  data() {
    return {
      currentStep: 0,
      submitting: false,
      loadingCases: false,
      loadingCustomers: false,
      entities: [],
      allUseCases: [],
      currentOrgExtendUseCase: null,
      appSettings: null,
      existingCases: [],
      customerList: [],
      selectedCaseOption: null,
      caseSearchQuery: '',
      customerSearchQuery: '',
      isSearchDropdownOpen: false,
      isCustomerDropdownOpen: false,
      caseFieldsVisible: false,
      customerFieldsVisible: false,
      isExistingCaseSelected: false,
      isExistingCustomerSelected: false,
      selectedExistingCase: null,
      selectedCustomer: null,
      successModalOpen: false,
      createdTaskInfo: null,
      taskSettings: null,
      orgConfig: null,

      form: {
        selectedUseCase: 'collection_management',
        subCaseId: '',
        caseId: null,
        customerId: null,
        customerFullName: '',
        customerMobile: '',
        customerAlternateMobile: '',
        caseNo: '',
        entity: '',
        typeOfLoan: 'Personal Loan',
        bkt: 'BKT 1',
        pos: '',
        emiAmount: '',
        productInfo: '',
        tenure: '',
        addressLine1: '',
        city: '',
        state: '',
        pincode: '',
        taskTitle: '',
        taskDescription: '',
        taskStartDateTime: dayjs().format('YYYY-MM-DDTHH:mm'),
        taskEndDateTime: dayjs().hour(23).minute(59).format('YYYY-MM-DDTHH:mm')
      },
      loanTypeOptions: [
        'Personal Loan',
        'Home Loan',
        'Auto Loan',
        'Credit Card',
        'Business Loan',
        'Two Wheeler Loan',
        'Gold Loan'
      ],
      bucketOptions: ['BKT 1', 'BKT 2', 'BKT 3', 'BKT 4', 'BKT 5', 'BKT 6+']
    }
  },
  computed: {
    stepperStepsConfig() {
      if (!this.appSettings || !this.appSettings.stepperSteps) {
        return {
          caseType: { required: true, visibility: true, skip: false },
          addOrSelectCustomer: { required: true, visibility: true, skip: false },
          customerAddress: { required: true, visibility: true, skip: false },
          taskTitle: { required: true, visibility: true, skip: false },
          caseStartAndEndTime: { required: true, visibility: true, skip: false }
        }
      }
      return this.appSettings.stepperSteps
    },
    hasAnyVisibleCaseField() {
      const fm = this.fieldMappings
      return !!(
        fm.entity?.visible ||
        fm.typeOfLoan?.visible ||
        fm.bkt?.visible ||
        fm.pos?.visible ||
        fm.emiAmount?.visible ||
        fm.tenure?.visible ||
        fm.productInfo?.visible
      )
    },
    activeSteps() {
      const cfg = this.stepperStepsConfig

      // If an existing case is selected, skip customer & address steps (aligned with mobile app)
      if (this.isExistingCaseSelected) {
        return [
          { key: 'case', title: 'Case Selection', icon: 'mdi-folder-outline' },
          { key: 'schedule', title: 'Task Details', icon: 'mdi-calendar-clock-outline' }
        ]
      }

      const allSteps = [
        {
          key: 'case',
          title: 'Case Selection',
          icon: 'mdi-folder-outline',
          visible: cfg.caseType?.visibility !== false
        },
        {
          key: 'customer',
          title: 'Customer Details',
          icon: 'mdi-account-outline',
          visible: cfg.addOrSelectCustomer?.visibility !== false
        },
        {
          key: 'address',
          title: 'Address & GPS',
          icon: 'mdi-map-marker-outline',
          visible: cfg.customerAddress?.visibility !== false
        },
        {
          key: 'schedule',
          title: 'Task Details',
          icon: 'mdi-calendar-clock-outline',
          visible: cfg.taskTitle?.visibility !== false || cfg.caseStartAndEndTime?.visibility !== false
        }
      ]

      return allSteps.filter((s) => s.visible)
    },
    currentStepKey() {
      return this.activeSteps[this.currentStep]?.key || 'case'
    },
    customerStepTitle() {
      return this.fieldMappings?.customerFullName?.label || 'Customer Information'
    },
    addressStepTitle() {
      return this.fieldMappings?.address?.label || 'Address & Geolocation'
    },
    subCaseOptions() {
      if (!this.allUseCases || !this.allUseCases.length) return []
      return this.allUseCases.map((u) => ({
        label: u.name,
        value: u._id,
        useCase: u.useCase
      }))
    },
    selectedSubCaseLabel() {
      const found = this.subCaseOptions.find((s) => s.value === this.form.subCaseId)
      return found?.label || 'Field Visit'
    },
    existingCaseOptions() {
      const q = (this.caseSearchQuery || '').trim().toLowerCase()
      if (!q) return this.existingCases
      return this.existingCases.filter((c) => {
        const caseNo = (c.caseNo || '').toLowerCase()
        const custName = (c.customer?.customerFullName || c.customerFullName || '').toLowerCase()
        const custMobile = (c.customer?.customerMobile?.[0] || c.mobile || '').toLowerCase()
        return caseNo.includes(q) || custName.includes(q) || custMobile.includes(q)
      })
    },
    entityOptions() {
      if (!this.entities || !this.entities.length) return []
      return this.entities.map((e) => ({
        label: e.entityName || e.name,
        value: e._id || e.id
      }))
    },
    fieldMappings() {
      const ext = this.currentOrgExtendUseCase
      const dynamicKeyMap = {
        collection_management: 'collectionManagementFieldMapping',
        loan_management: 'loanManagementFieldMapping'
      }
      const specificFieldMapping = ext?.useCase && dynamicKeyMap[ext.useCase] ? (ext[dynamicKeyMap[ext.useCase]] || {}) : {}

      const mappings = {
        ...(ext?.baseFieldMappings || {}),
        ...(ext?.customerFieldMappings || {}),
        ...specificFieldMapping
      }

      const entityConfig = ext?.entity || {}

      const getField = (fieldKey, defaultLabel, defaultVisible = false, defaultRequired = false) => {
        const item = mappings?.[fieldKey]
        return {
          label: item?.displayName || defaultLabel,
          visible: item?.formVisibility !== undefined ? !!item.formVisibility : defaultVisible,
          required: item?.required !== undefined ? !!item.required : defaultRequired
        }
      }

      return {
        entity: {
          label: entityConfig?.displayName || 'Entity / Bank',
          visible: entityConfig?.enabled !== undefined ? !!entityConfig.enabled : false,
          required: false
        },
        caseNo: getField('caseNo', 'Case Number / Loan ID', true, true),
        crn: getField('crn', 'CRN / Customer ID', false, false),
        customerFullName: getField('customerFullName', 'Customer Full Name', true, true),
        typeOfLoan: getField('typeOfLoan', 'Type of Loan', false, false),
        bkt: {
          label: mappings?.bucket?.displayName || mappings?.bkt?.displayName || 'Bucket (BKT)',
          visible: mappings?.bucket?.formVisibility !== undefined ? !!mappings.bucket.formVisibility : (mappings?.bkt?.formVisibility !== undefined ? !!mappings.bkt.formVisibility : false),
          required: !!(mappings?.bucket?.required || mappings?.bkt?.required)
        },
        pos: {
          label: mappings?.principalOutstanding?.displayName || mappings?.pos?.displayName || 'Principal Outstanding (POS)',
          visible: mappings?.principalOutstanding?.formVisibility !== undefined ? !!mappings.principalOutstanding.formVisibility : (mappings?.pos?.formVisibility !== undefined ? !!mappings.pos.formVisibility : false),
          required: !!(mappings?.principalOutstanding?.required || mappings?.pos?.required)
        },
        emiAmount: getField('emiAmount', 'EMI Amount', false, false),
        tenure: getField('tenure', 'Tenure', false, false),
        productInfo: getField('productInfo', 'Product Info', false, false),
        customerMobile: getField('customerMobile', 'Mobile Number', true, true),
        customerAlternateMobile: getField('customerAlternateMobile', 'Alternate Phone', false, false),
        address: getField('address', 'Street Address', true, true),
        city: getField('city', 'City', true, true),
        state: getField('state', 'State', false, false),
        pincode: getField('pincode', 'Pincode', true, true)
      }
    },
    mobileRules() {
      const isReq = this.fieldMappings?.customerMobile?.required
      return [
        (v) => (!isReq || !!(v && v.trim())) || 'Mobile number is required',
        (v) => !v || /^[0-9]{10}$/.test(v.trim()) || 'Enter a valid 10-digit mobile number'
      ]
    },
    altMobileRules() {
      const isReq = this.fieldMappings?.customerAlternateMobile?.required
      return [
        (v) => (!isReq || !!(v && v.trim())) || 'Alternate mobile number is required',
        (v) => !v || /^[0-9]{10}$/.test(v.trim()) || 'Enter a valid 10-digit mobile number'
      ]
    }
  },
  watch: {
    caseSearchQuery: debounce(function (query) {
      if (query && typeof query === 'string' && query.length >= 2) {
        this.fetchExistingCases(query)
      }
    }, 300),
    customerSearchQuery: debounce(function (query) {
      if (query && typeof query === 'string' && query.length >= 2) {
        this.fetchCustomers(query)
      }
    }, 300)
  },
  mounted() {
    if (!this.form.crn) {
      this.form.crn = this.generateCRN()
    }
    this.initFormData()
  },
  methods: {
    formatAmount(amount) {
      return convertNumberToAmount(amount)
    },
    async fetchCustomers(search) {
      try {
        this.loadingCustomers = true
        const res = await getCustomers({
          search: search || ''
        })
        const list = res.data?.customers || res.data?.data || []
        this.customerList = Array.isArray(list) ? list : []
      } catch (e) {
        console.warn('Error fetching customers:', e)
        this.customerList = []
      } finally {
        this.loadingCustomers = false
      }
    },
    handleCustomerSearchInput() {
      this.isCustomerDropdownOpen = true
    },
    onSelectCustomer(cust) {
      if (!cust) return
      this.isCustomerDropdownOpen = false
      this.isExistingCustomerSelected = true
      this.customerFieldsVisible = false
      this.selectedCustomer = cust
      this.form.customerId = cust._id || cust.id || null
      this.form.customerFullName = cust.customerFullName || `${cust.firstName || ''} ${cust.lastName || ''}`.trim() || ''
      this.form.crn = cust.crn || this.form.crn || ''
      this.form.customerMobile = cust.customerMobile?.[0] || cust.mobile || ''
      if (cust.customerMobile?.[1]) {
        this.form.customerAlternateMobile = cust.customerMobile[1]
      }

      // Auto-fill address from customer address if present
      const addr = cust.customerAddress?.[0] || {}
      if (addr.addressLine1 || addr.address) {
        this.form.addressLine1 = addr.addressLine1 || addr.address || ''
      }
      if (addr.city) {
        this.form.city = addr.city || ''
      }
      if (addr.state) {
        this.form.state = addr.state || ''
      }
      if (addr.pinCode || addr.pincode) {
        this.form.pincode = addr.pinCode || addr.pincode || ''
      }

      if (!this.form.taskTitle) {
        this.form.taskTitle = `Visit - ${this.form.customerFullName}`
      }
      this.customerSearchQuery = this.form.customerFullName
    },
    generateCRN() {
      const timestamp = Date.now().toString().slice(-6)
      const random = Math.floor(1000 + Math.random() * 9000)
      return `CRN${timestamp}${random}`
    },
    onAddNewCustomer(name) {
      const cleanName = (name || '').trim()
      this.isCustomerDropdownOpen = false
      this.isExistingCustomerSelected = false
      this.customerFieldsVisible = true
      this.selectedCustomer = null
      this.form.customerId = null
      this.form.customerFullName = cleanName
      if (!this.form.crn) {
        this.form.crn = this.generateCRN()
      }
      this.customerSearchQuery = cleanName
    },
    clearCustomerSelection() {
      this.isExistingCustomerSelected = false
      this.selectedCustomer = null
      this.customerFieldsVisible = false
      this.form.customerId = null
      this.form.customerFullName = ''
      this.form.crn = this.generateCRN()
      this.form.customerMobile = ''
      this.form.customerAlternateMobile = ''
      this.customerSearchQuery = ''
      this.isCustomerDropdownOpen = false
    },
    async initFormData() {
      try {
        const [entityRes, useCasesRes, appSettingsRes, taskSettingsRes, orgConfigRes] = await Promise.allSettled([
          getEntities({ search: '' }),
          getAllUseCases(),
          getEmpAppSettings(),
          getTaskSettings(),
          getOrgConfig()
        ])
        if (taskSettingsRes.status === 'fulfilled') {
          const s = taskSettingsRes.value.data
          this.taskSettings = s?.data || s?.taskSettings || s
        }
        if (orgConfigRes.status === 'fulfilled') {
          const o = orgConfigRes.value.data
          this.orgConfig = o?.data || o?.orgConfig || o
        }
        if (entityRes.status === 'fulfilled') {
          this.entities = entityRes.value.data?.entities || entityRes.value.data?.data || []
        }
        if (appSettingsRes.status === 'fulfilled') {
          this.appSettings = appSettingsRes.value.data?.empApp || appSettingsRes.value.data?.data || appSettingsRes.value.data || null
        }
        if (useCasesRes.status === 'fulfilled') {
          const list = useCasesRes.value.data?.data || useCasesRes.value.data || []
          this.allUseCases = Array.isArray(list) ? list : []
          if (this.allUseCases.length > 0) {
            this.form.subCaseId = this.allUseCases[0]._id
            await this.handleSubCaseChange(this.form.subCaseId)
          }
        }
      } catch (err) {
        console.warn('Error fetching metadata:', err)
      }
    },
    async handleSubCaseChange(subCaseId) {
      if (!subCaseId) return
      try {
        // Fetch subcase field visibility configs
        const res = await getExtendUseCaseById(subCaseId)
        this.currentOrgExtendUseCase = res.data?.data || res.data

        // Clear existing case selection
        this.selectedCaseOption = null
        this.isExistingCaseSelected = false
        this.caseFieldsVisible = false
        this.selectedExistingCase = null
        this.form.caseId = null

        // Fetch initial list of cases for this subcase
        await this.fetchExistingCases('')
      } catch (e) {
        console.warn('Could not fetch subcase extend configuration:', e)
        this.currentOrgExtendUseCase = null
      }
    },
    async fetchExistingCases(search) {
      try {
        this.loadingCases = true
        const res = await getCollectionCases({
          subCase: this.form.subCaseId,
          search: search || ''
        })
        const list = res.data?.collections || res.data?.cases || res.data?.data || []
        this.existingCases = Array.isArray(list) ? list : []
      } catch (e) {
        console.warn('Error fetching cases:', e)
        this.existingCases = []
      } finally {
        this.loadingCases = false
      }
    },
    handleCaseSearchInput() {
      this.isSearchDropdownOpen = true
    },
    onSelectExistingCase(c) {
      if (!c) return
      this.isSearchDropdownOpen = false
      this.isExistingCaseSelected = true
      this.caseFieldsVisible = false
      this.selectedExistingCase = c
      this.form.caseId = c._id
      this.form.caseNo = c.caseNo || ''
      this.form.typeOfLoan = c.typeOfLoan || this.form.typeOfLoan
      this.form.bkt = c.bkt || this.form.bkt
      this.form.pos = c.pos || this.form.pos
      this.form.emiAmount = c.emiAmount || this.form.emiAmount
      this.form.tenure = c.tenure || this.form.tenure
      this.form.productInfo = c.productInfo || this.form.productInfo
      if (c.entity?._id || c.entity) {
        this.form.entity = c.entity?._id || c.entity
      }

      // Auto-fill customer info if present
      const cust = c.customer || {}
      this.form.customerId = cust._id || cust.id || null
      this.form.customerFullName = cust.customerFullName || `${cust.firstName || ''} ${cust.lastName || ''}`.trim() || c.customerFullName || ''
      this.form.customerMobile = cust.customerMobile?.[0] || cust.mobile || ''

      // Auto-fill address if present
      const addr = cust.customerAddress?.[0] || {}
      this.form.addressLine1 = addr.addressLine1 || addr.address || ''
      this.form.city = addr.city || ''
      this.form.state = addr.state || ''
      this.form.pincode = addr.pinCode || addr.pincode || ''

      // Auto-suggest task title
      this.form.taskTitle = `Visit - ${this.form.customerFullName || c.caseNo}`
      this.caseSearchQuery = c.caseNo || ''
    },
    onAddNewCaseNo(caseNumber) {
      const cleanNo = (caseNumber || '').trim()
      if (!cleanNo) return
      this.isSearchDropdownOpen = false
      this.isExistingCaseSelected = false
      this.caseFieldsVisible = true
      this.selectedExistingCase = null
      this.form.caseId = null
      this.form.caseNo = cleanNo
      this.caseSearchQuery = cleanNo
    },
    clearCaseSelection() {
      this.isExistingCaseSelected = false
      this.selectedExistingCase = null
      this.caseFieldsVisible = false
      this.form.caseNo = ''
      this.form.caseId = null
      this.caseSearchQuery = ''
      this.isSearchDropdownOpen = false
    },
    canJumpToStep(targetIndex) {
      return targetIndex < this.currentStep
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },
    validateField(fieldKey, value, customMsg = null) {
      const field = this.fieldMappings[fieldKey]
      if (!field?.visible || !field?.required) return true
      const val = typeof value === 'string' ? value.trim() : value
      if (val === '' || val === null || val === undefined) {
        this.$toast?.error?.(customMsg || `Please enter ${field.label}`)
        return false
      }
      return true
    },
    validatePhoneField(fieldKey, value) {
      const field = this.fieldMappings[fieldKey]
      const phone = (value || '').trim()
      if (field?.visible && field?.required && !phone) {
        this.$toast?.error?.(`Please enter ${field.label}`)
        return false
      }
      if (phone && !/^[0-9]{10}$/.test(phone)) {
        this.$toast?.error?.(`Please enter a valid 10-digit ${field?.label || 'mobile number'} (digits only)`)
        return false
      }
      return true
    },
    onMobileInput(fieldKey, val) {
      if (typeof val === 'string') {
        this.form[fieldKey] = val.replace(/\D/g, '').slice(0, 10)
      }
    },
    nextStep() {
      const stepKey = this.currentStepKey

      if (stepKey === 'case') {
        if (!this.form.subCaseId) {
          this.$toast?.error?.('Please select a sub-case category')
          return
        }
        if (!this.validateField('caseNo', this.form.caseNo, `Please enter or select ${this.fieldMappings.caseNo.label}`)) return

        if (this.caseFieldsVisible) {
          if (!this.validateField('entity', this.form.entity, `Please select ${this.fieldMappings.entity.label}`)) return
          if (!this.validateField('typeOfLoan', this.form.typeOfLoan, `Please select ${this.fieldMappings.typeOfLoan.label}`)) return
          if (!this.validateField('bkt', this.form.bkt, `Please select ${this.fieldMappings.bkt.label}`)) return
          if (!this.validateField('pos', this.form.pos)) return
          if (!this.validateField('emiAmount', this.form.emiAmount)) return
          if (!this.validateField('tenure', this.form.tenure)) return
          if (!this.validateField('productInfo', this.form.productInfo)) return
        }
      } else if (stepKey === 'customer') {
        if (!this.validateField('customerFullName', this.form.customerFullName)) return
        if (!this.validateField('crn', this.form.crn)) return
        if (!this.validatePhoneField('customerMobile', this.form.customerMobile)) return
        if (!this.validatePhoneField('customerAlternateMobile', this.form.customerAlternateMobile)) return
      } else if (stepKey === 'address') {
        if (!this.validateField('address', this.form.addressLine1)) return
        if (!this.validateField('city', this.form.city)) return
        if (!this.validateField('state', this.form.state)) return
        if (!this.validateField('pincode', this.form.pincode)) return
      }

      this.currentStep++
    },
    async submitTask() {
      if (!this.form.taskTitle.trim()) {
        this.form.taskTitle = `${this.form.customerFullName || 'Visit'} - ${this.form.caseNo || this.selectedSubCaseLabel}`
      }
      const isStartEndTimeRequired = this.stepperStepsConfig?.caseStartAndEndTime?.visibility !== false && this.stepperStepsConfig?.caseStartAndEndTime?.required !== false
      if (isStartEndTimeRequired && (!this.form.taskStartDateTime || !this.form.taskEndDateTime)) {
        this.$toast?.error?.('Please specify task start and end times')
        return
      }
      if (!this.form.taskStartDateTime) {
        this.form.taskStartDateTime = new Date().toISOString()
      }
      if (!this.form.taskEndDateTime) {
        this.form.taskEndDateTime = new Date(Date.now() + 60 * 60 * 1000).toISOString()
      }

      try {
        this.submitting = true
        const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id || this.$storage?.getUniversal('user')?.user?._id || this.$storage?.getUniversal('user')?.id

        let createdCaseId = this.form.caseId

        // If an existing case is selected:
        // Update case assignedTo (ensure current user is assigned) and set status to 'IN_PROGRESS'
        if (this.isExistingCaseSelected && createdCaseId) {
          const existingAssigned = (this.selectedExistingCase?.assignedTo || []).map((a) => (a?._id || a?.id || a)).filter(Boolean)
          const isAlreadyAssigned = existingAssigned.includes(userId)
          const updatedAssigned = isAlreadyAssigned ? existingAssigned : [...existingAssigned, userId]

          try {
            await updateCaseStatus({
              caseId: createdCaseId,
              assignedTo: updatedAssigned,
              status: 'IN_PROGRESS'
            })
          } catch (updateErr) {
            console.warn('Case status/assignedTo update fallback:', updateErr)
          }
        } else {
          // 1. Create Customer record ONLY IF customer step is enabled in config and new customer info is provided
          let createdCustomerId = this.form.customerId
          const isCustomerStepEnabled = this.stepperStepsConfig?.addOrSelectCustomer?.visibility !== false

          if (!createdCustomerId && isCustomerStepEnabled && (this.form.customerMobile || this.form.customerFullName)) {
            const rawLine1 = (this.form.addressLine1 || '').trim()
            const city = (this.form.city || '').trim()
            const state = (this.form.state || '').trim()
            const pincode = (this.form.pincode || '').trim()

            // Backend requires addressLine1 for each address in customerAddress
            const hasAddressInfo = !!(rawLine1 || city || state || pincode)
            const addressLine1 = rawLine1 || (city ? `${city}, ${state || ''}`.trim() : 'Primary Address')

            const validMobiles = []
            if (this.form.customerMobile) {
              const cleanMobile = this.form.customerMobile.replace(/\D/g, '')
              if (cleanMobile.length !== 10) {
                this.$toast?.error?.('Customer mobile number must be exactly 10 digits')
                this.submitting = false
                return
              }
              validMobiles.push(cleanMobile)
            }
            if (this.form.customerAlternateMobile) {
              const cleanAltMobile = this.form.customerAlternateMobile.replace(/\D/g, '')
              if (cleanAltMobile.length !== 10) {
                this.$toast?.error?.('Alternate mobile number must be exactly 10 digits')
                this.submitting = false
                return
              }
              if (!validMobiles.includes(cleanAltMobile)) {
                validMobiles.push(cleanAltMobile)
              }
            }

            const customerPayload = {
              crn: this.form.crn || this.form.customerMobile || this.form.customerFullName,
              customerFullName: this.form.customerFullName,
              customerMobile: validMobiles
            }

            if (hasAddressInfo) {
              customerPayload.customerAddress = [
                {
                  typeOfAddress: 'CURRENT_ADDRESS',
                  addressLine1: addressLine1,
                  address: addressLine1,
                  city: city || undefined,
                  state: state || undefined,
                  pinCode: pincode || undefined,
                  pincode: pincode || undefined
                }
              ]
            }

            try {
              const custRes = await createCustomer(customerPayload)
              createdCustomerId = custRes.data?.customer?._id || custRes.data?.data?._id || custRes.data?._id
            } catch (custErr) {
              console.error('Customer create failed:', custErr)
              const errMsg = custErr.response?.data?.message || custErr.message || 'Failed to create customer'
              this.$toast?.error?.(errMsg)
              this.submitting = false
              return
            }
          }

          // 2. Create Collection Case record with status: 'OPEN' and assignedTo: [userId]
          const selectedSubCase = this.subCaseOptions.find((s) => s.value === this.form.subCaseId)
          const casePayload = {
            assignedTo: [userId],
            status: 'OPEN',
            caseNo: this.form.caseNo || `CASE-${Date.now().toString().slice(-6)}`,
            caseType: selectedSubCase?.label || 'Field Visit',
            subCase: this.form.subCaseId || undefined,
            useCase: selectedSubCase?.useCase || 'collection_management',
            customer: createdCustomerId || undefined,
            entity: this.form.entity || undefined,
            typeOfLoan: this.form.typeOfLoan,
            bkt: this.form.bkt,
            productInfo: this.form.productInfo,
            tenure: this.form.tenure,
            pos: this.form.pos ? Number(this.form.pos) : undefined,
            emiAmount: this.form.emiAmount ? Number(this.form.emiAmount) : undefined
          }

          try {
            const caseRes = await createCollectionCase(casePayload)
            createdCaseId = caseRes.data?.data?._id || caseRes.data?.case?._id || caseRes.data?._id
          } catch (caseErr) {
            console.warn('Case creation fallback:', caseErr)
          }
        }

        // 3. Create Case Task for Employee with assignedTo: userId
        const taskPayload = {
          assignedTo: userId,
          case: createdCaseId || undefined,
          caseType: 'collection',
          meetingDetails: {},
          taskTitle: this.form.taskTitle || this.form.caseNo || this.selectedSubCaseLabel,
          taskDescription: this.form.taskDescription || '',
          taskStartDateTime: this.form.taskStartDateTime,
          taskEndDateTime: this.form.taskEndDateTime,
          taskStatus: 'PENDING'
        }

        let taskResponse = null
        try {
          taskResponse = await createCollectionCaseTask(taskPayload)
        } catch (e) {
          taskResponse = await createNewTask(taskPayload)
        }

        const taskData = taskResponse?.data?.task || taskResponse?.data?.data || taskResponse?.data || {}
        const createdTaskId = taskData?._id || taskData?.id || createdCaseId

        this.createdTaskInfo = {
          taskId: createdTaskId,
          caseId: createdCaseId,
          isCollection: true,
          title: this.form.taskTitle || this.form.caseNo || this.selectedSubCaseLabel,
          taskData: taskData
        }

        this.$toast?.success?.('Task created successfully!')

        // Check if direct visit start is configured in appSettings
        if (this.appSettings?.isDirectVisitStartAllowed) {
          this.proceedWithTask()
        } else {
          // Open Success Action Modal matching mobile app flow
          this.successModalOpen = true
        }
      } catch (err) {
        console.error('Error submitting new task:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to create task')
      } finally {
        this.submitting = false
      }
    },
    proceedWithTask() {
      this.successModalOpen = false
      const targetId = this.createdTaskInfo?.taskId || this.createdTaskInfo?.caseId || this.form.caseId
      if (!targetId) {
        this.goToDashboard()
        return
      }

      // Compute step routes based on organization taskCompletionStepsConfig
      const configuredSteps = computeTaskSteps(this.taskSettings, this.orgConfig)
      const firstStepRoute = configuredSteps && configuredSteps.length > 0 ? configuredSteps[0].routeName : 'AppStartJob'

      this.$router.push({
        name: firstStepRoute,
        params: { taskId: targetId },
        query: { isCollection: true }
      })
    },
    goToDashboard() {
      this.successModalOpen = false
      this.$router.push('/app/dashboard')
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>
